import React from 'react';
import { Calculator } from 'lucide-react';
import { CalculationResults as Results, CalculationMode } from '../../types/gasTypes';

/**
 * Props interface for the CalculationResults component
 */
interface CalculationResultsProps {
  /** Calculation results data */
  results: Results;
  /** Current calculation mode */
  calculationMode: CalculationMode;
  /** Target alcohol percentage (for alcohol mode) */
  targetAlcoholPercent?: number;
  /** Target octane rating (for octane mode) */
  targetOctane?: number;
}

/**
 * CalculationResults Component
 * Displays the calculation results including fuel quantities, summary, and final mixture properties
 */
const CalculationResultsDisplay: React.FC<CalculationResultsProps> = ({
  results,
  calculationMode,
  targetAlcoholPercent,
  targetOctane
}) => {
  // Filter mixtures that have volume > 0
  const usedMixtures = results.mixtures.filter(m => m.volume > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Calculator className="h-6 w-6" style={{ color: 'var(--interactive-success)' }} />
        <h3 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          Calculation Results
        </h3>
      </div>
      
      {/* Fuel Quantities Section */}
      <div>
        <h4 className="font-semibold mb-4 text-lg" style={{ color: 'var(--text-primary)' }}>
          Fuel Quantities
        </h4>
        <div className="space-y-3">
          {usedMixtures.map((mixture) => (
            <div 
              key={mixture.id} 
              className="card-compact flex justify-between items-center"
            >
              <div>
                <div className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                  {mixture.name}
                </div>
                <div className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                  {mixture.alcoholPercent}% alcohol • {mixture.octaneRating} octane
                </div>
              </div>
              <div className="text-right">
                <div className="text-lg sm:text-2xl font-bold" style={{ color: 'var(--interactive-primary)' }}>
                  {mixture.volume.toFixed(2)} L
                </div>
                <div className="text-sm sm:text-lg font-medium" style={{ color: 'var(--text-primary)' }}>
                  €{mixture.cost.toFixed(2)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
        
      {/* Summary Section */}
      <div>
        <h4 className="font-semibold mb-4 text-lg" style={{ color: 'var(--text-primary)' }}>
          Summary
        </h4>
        <div className="card-compact space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-6">
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>Total Volume:</span>
                <span className="font-bold text-base sm:text-lg" style={{ color: 'var(--text-primary)' }}>
                  {results.totalVolume.toFixed(2)} L
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>Total Cost:</span>
                <span className="font-bold text-base sm:text-lg" style={{ color: 'var(--interactive-success)' }}>
                  €{results.totalCost.toFixed(2)}
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>Cost per Liter:</span>
                <span className="font-bold text-base sm:text-lg" style={{ color: 'var(--text-primary)' }}>
                  €{(results.totalCost / results.totalVolume).toFixed(3)}
                </span>
              </div>
            </div>

            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>Final Alcohol %:</span>
                <span className="font-bold text-base sm:text-lg" style={{ color: 'var(--interactive-primary)' }}>
                  {results.actualAlcoholPercent.toFixed(1)}%
                </span>
              </div>
              
              <div className="flex justify-between items-center">
                <span className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>Final Octane:</span>
                <span className="font-bold text-base sm:text-lg" style={{ color: 'var(--interactive-primary)' }}>
                  {results.actualOctane.toFixed(1)}
                </span>
              </div>
              
              {results.remainingBudget > 0 && (
                <div className="flex justify-between items-center">
                  <span className="font-medium text-sm sm:text-base" style={{ color: 'var(--text-secondary)' }}>Remaining Budget:</span>
                  <span className="font-bold text-lg" style={{ color: 'var(--interactive-success)' }}>
                    €{results.remainingBudget.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Footer Description */}
          <div className="pt-4 border-t" style={{ borderColor: 'var(--border-primary)' }}>
            <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              {calculationMode === 'alcohol' && targetAlcoholPercent !== undefined
                ? `Target: ${targetAlcoholPercent}% alcohol content`
                : calculationMode === 'octane' && targetOctane !== undefined
                ? `Target: ${targetOctane} octane rating`
                : 'Optimal mixture calculated'
              }
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CalculationResultsDisplay;